import { isEmpty, mergeWith, isNull } from 'lodash-es'
import { getPhoneNumberFormat } from 'ppbl/utils/locale-helper'
import serverData from 'ppbl/utils/server-data'
import { applicationCompleteRoutes } from './application/constants/routes'
import {
  CaseTypes,
  LEAD_ORIGIN_URL,
  PARTNER_CAMP_EXPIRY,
  PARTNER_CAMP_EXPIRY_DAYS,
  PARTNER_CAMP_ID,
  UTM_CAMPAIGN,
  UTM_CAMPAIGN_DETAILS,
  errorConstants,
} from 'ppbl/utils/constants'
import clientLogger from 'ppbl/utils/client-logger'
import { navigate } from '@reach/router'
import { sanitizeInput } from '../utils/string-helper'

const currencyCodes = require('currency-codes')

export const toCommaSeparatedNumber = (number, radix) => {
  return Number(parseFloat(number).toFixed(radix)).toLocaleString('en', {
    minimumFractionDigits: radix,
  })
}

export const currencyParser = (currencyString) => {
  return parseFloat(currencyString.replace(/[^\d.]/g, ''))
}

export const scrollToTopOnContinue = () => {
  scroll({
    top: 100,
    behavior: 'smooth',
  })
}

export const lbLogoUrl = `${serverData.context?.resourceBaseUrl}/images/loanBuilder-monogram.png`

export const getProductIdentifier = (product) => {
  if (product === 'paypal' || product === 'paypal-renewal') {
    return 'PPBL_US'
  }

  return 'LOAN_BUILDER_US'
}

export const getRenewalStatus = (product) => {
  if (product === 'paypal-renewal' || product === 'app-renewal') {
    return 'LOAN_RENEWAL'
  }
  return 'NEW_LOAN'
}

export const isRenewalFlow = (product) => {
  return getRenewalStatus(product) === 'LOAN_RENEWAL'
}

export const getDisclosureStatus = (region) => {
  if (region === 'CA' || region === 'NY') {
    return true
  }
  return false
}

export const numberFormatter = (numberString) =>
  ('' + numberString).replace(/\D/g, '')

const dateFormatter = (dateString) => {
  const [_, month, year] = /(\d+)\/(\d*)/.exec(dateString)
  return `${year}-${month.toString().padStart(2, '0')}`
}

export function parseCurrencyString(revenueString) {
  let currencyCode = 'USD' // Default currency code
  const value = revenueString.replace(/[^\d.,]/g, '')

  const symbolMatch = revenueString.match(/[^\d.,]+/)
  if (symbolMatch) {
    const symbolWithoutSpaces = symbolMatch[0].trim()
    const data = currencyCodes.code(symbolWithoutSpaces)
    if (data) {
      currencyCode = data.code
    }
  } else {
    const data = currencyCodes.code(revenueString.toUpperCase())
    if (data) {
      currencyCode = data.code
    }
  }
  return {
    currencyCode,
    value,
  }
}

export const getApplicationFieldsData = (
  applicationFields,
  cachedData,
  applicationFieldValues,
) => {
  let data = {}

  applicationFields.forEach((applicationField) => {
    if (Array.isArray(applicationField)) {
      applicationField.forEach((field) => {
        data[field.name] =
          applicationFieldValues?.[field.name] || cachedData[field.name]
      })
    } else {
      data[applicationField.name] =
        applicationFieldValues?.[applicationField.name] ||
        cachedData[applicationField.name]
    }
  })

  return data
}

// for Big Commerce Campaign
export const getPartnerCampId = () => {
  const campaignExpiryTimestamp = localStorage.getItem(PARTNER_CAMP_EXPIRY)
  const campaignIdFromLocalStorage = localStorage.getItem(PARTNER_CAMP_ID)

  const now = new Date()
  const currentUTCTimestamp = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds(),
    now.getUTCMilliseconds(),
  )

  if (currentUTCTimestamp <= campaignExpiryTimestamp) {
    return campaignIdFromLocalStorage
  }

  localStorage.removeItem(PARTNER_CAMP_ID)
  localStorage.removeItem(PARTNER_CAMP_EXPIRY)

  return null
}

const validateCampaign = (campaignId) => {
  // Return false if campaignId is null, undefined or empty
  if (!campaignId) {
    clientLogger.info(
      'Invalid campaign id for application creation : null or undefined',
    )
    return false
  }

  // Check length - must be 18 characters or less
  if (campaignId.length > 18) {
    clientLogger.info(
      `Invalid campaign id for application creation: ${campaignId} (too long)`,
    )
    return false
  }

  // Check if alphanumeric only using regex
  const alphanumericRegex = /^[a-zA-Z0-9]+$/
  if (!alphanumericRegex.test(campaignId)) {
    clientLogger.info(
      `Invalid campaign id for application creation: ${campaignId} (wrong format)`,
    )
    return false
  }

  // All validations passed
  return true
}

export const createApplicationPayloadGenerator = (data) => {
  const isRenewalApplication =
    serverData.startParams.product === 'app-renewal' ||
    serverData.startParams.product === 'paypal-renewal'

  let campaignId = sessionStorage.getItem(UTM_CAMPAIGN)

  if (!campaignId) {
    // Big C flow
    campaignId = getPartnerCampId()
  }
  campaignId = campaignId && sanitizeInput(campaignId)

  const originationUrl = sessionStorage.getItem(LEAD_ORIGIN_URL)
  const selectedCampaignId = campaignId || serverData.startParams.utm_campaign

  return {
    flowContext: {
      flowSpecifier: 'STANDALONE_APPLICATION',
      attributes: [getRenewalStatus(serverData.startParams.product)],
    },
    creditProductIdentifier: getProductIdentifier(
      serverData.startParams.product,
    ),
    originationUrl: originationUrl
      ? originationUrl
      : serverData.startParams.leadOriginUrl,
    countryCode: serverData.startParams.country.toUpperCase(),
    guarantor: {
      name: { givenName: data?.firstName, surname: data?.lastName },
      emailAddress: data?.emailAddress,
      phones: [
        {
          countryCode: '1',
          nationalNumber: numberFormatter(data?.personalPhoneNumber),
          type: data?.personalPhoneNumberType,
        },
      ],
    },
    loanPurpose: data?.primaryLoanPurpose,
    productDiscovery: {
      source: data?.howDidYouHearAboutThisProduct,
      invitationCode: data?.doYouHaveAFundingCode,
    },
    ...(isRenewalApplication && {
      portalAccess: {
        userToken: serverData.startParams.renewalToken,
      },
    }),
    consents: [
      {
        type: 'TERMS_AND_CONDITIONS',
        status: data?.agreedToTermsAndConditions ? 'ACCEPTED' : 'NOT_ACCEPTED',
      },
    ],
    business: {
      legalBusinessName: data?.legalBusinessName,
    },
    campaign: {
      id: validateCampaign(selectedCampaignId) ? selectedCampaignId : null,
    },
  }
}

export const updateApplicationPayloadGeneratorPage1 = (
  data,
  creditApplicationId,
) => {
  const applicationUpdateFields = {
    originationUrl: serverData.startParams.leadOriginUrl,
    guarantor: {
      name: { givenName: data?.firstName, surname: data?.lastName },
      emailAddress: data?.emailAddress,
      phones: [
        {
          countryCode: '1',
          nationalNumber: numberFormatter(data?.personalPhoneNumber),
          type: data?.personalPhoneNumberType,
        },
      ],
    },
    loanPurpose: data?.primaryLoanPurpose,
    productDiscovery: {
      source: data?.howDidYouHearAboutThisProduct,
      invitationCode: data?.doYouHaveAFundingCode,
    },
    consents: [
      {
        type: 'TERMS_AND_CONDITIONS',
        status: data?.agreedToTermsAndConditions ? 'ACCEPTED' : 'NOT_ACCEPTED',
      },
    ],
    business: {
      legalBusinessName: data?.legalBusinessName,
    },
  }

  return {
    id: creditApplicationId,
    applicationUpdateFields,
  }
}

export const updateApplicationPayloadGeneratorPage2 = (
  data,
  creditApplicationId,
) => {
  const applicationUpdateFields = {
    guarantor: {
      address: {
        addressLine1: data?.homeAddress1,
        addressLine2: data?.homeAddress2,
        adminArea2: data?.city,
        adminArea1: data?.state,
        postalCode: data?.zip,
        countryCode: serverData.startParams.country.toUpperCase(),
      },
    },
  }

  return {
    id: creditApplicationId,
    applicationUpdateFields,
  }
}

export const updateApplicationPayloadGeneratorPage3 = (
  data,
  legalBusinessName,
  creditApplicationId,
) => {
  const applicationUpdateFields = {
    business: {
      legalBusinessName,
      address: {
        addressLine1: data?.businessAddress1,
        addressLine2: data?.businessAddress2,
        adminArea2: data?.businessCity,
        adminArea1: data?.businessState,
        postalCode: data?.businessZip,
        countryCode: serverData.startParams.country.toUpperCase(),
      },
      phones: [
        {
          countryCode: '1',
          nationalNumber: numberFormatter(data?.businessPhoneNumber),
          type: 'BUSINESS',
        },
      ],
      websiteUrl: data?.businessWebAddress,
    },
  }

  return {
    id: creditApplicationId,
    applicationUpdateFields,
  }
}

export const updateApplicationPayloadGeneratorPage4 = (
  data,
  legalBusinessName,
  creditApplicationId,
) => {
  const revenue = parseCurrencyString(data?.totalAnnualBusinessRevenue)

  const cleanIndustryString = data?.businessIndustry.replace(/[^A-Za-z]/g, '')

  const applicationUpdateFields = {
    business: {
      organizationType: data?.businessEntityType,
      legalBusinessName,
      doingBusinessAsName:
        (data?.doingBusinessAsRadio === 'yes' && data?.doingBusinessAs) || null,
      incorporationRegion:
        (['CORPORATION', 'LIMITED_LIABILITY_COMPANY', 'PARTNERSHIP'].includes(
          data?.businessEntityType,
        ) &&
          data?.stateOfIncorporation) ||
        null,
      financialProfile: {
        totalAnnualBusinessRevenue: {
          currencyCode: revenue.currencyCode,
          value: numberFormatter(revenue.value),
        },
      },
      formationDate: dateFormatter(data?.businessStartDate),
      numberOfFullTimeEmployees: parseInt(data?.numberOfFullTimeEmployees, 10),
      industryType: cleanIndustryString.toUpperCase(),
      subIndustryCode: data?.businessSubIndustry,
    },
  }

  return {
    id: creditApplicationId,
    applicationUpdateFields,
  }
}

export const updateApplicationPayloadGeneratorPage5 = (
  data,
  legalBusinessName,
  creditApplicationId,
) => {
  const dob = new Date(data?.dateOfBirth)

  const applicationUpdateFields = {
    consents: [
      {
        type: 'ELECTRONIC_INFORMATION_CONSENT',
        status: data?.electronicCommunications ? 'ACCEPTED' : 'NOT_ACCEPTED',
      },
      {
        type: 'SHARING_INFORMATION_CONSENT',
        status: data?.writtenInstructions ? 'ACCEPTED' : 'NOT_ACCEPTED',
      },
    ],
    guarantor: {
      birthDate: dob.toISOString().split('T')[0],
      identifications: {
        socialSecurityNumber: numberFormatter(data?.socialSecurityNumber),
      },
      businessAssociation: {
        ownershipPercentage: parseInt(data?.percentageOfOwnership, 10),
        roles: data?.isControllingManager ? ['COMPANY_EXECUTIVE'] : [],
      },
    },
    business: {
      legalBusinessName,
      identifications: {
        federalTaxId: numberFormatter(data?.federalTaxId),
      },
    },
  }

  return {
    id: creditApplicationId,
    applicationUpdateFields,
  }
}

export const submitApplicationPayloadGenerator = (creditApplicationId) => {
  return {
    applicationId: creditApplicationId,
  }
}

export const getApplicationByIdPollingPayloadGenerator = (
  creditApplicationId,
) => {
  return creditApplicationId
}

// eslint-disable-next-line complexity
export const determineSkipGetApplicationCall = (
  contextData,
  mostRecentCompletedPage,
  applicationSubmitStatus,
) => {
  let skipCall = true

  if (isEmpty(contextData)) {
    return false
  }

  if (contextData && isEmpty(contextData.applicationMetaData)) {
    return false
  }

  const metaData = contextData.applicationMetaData

  if (
    metaData.applicationId === undefined ||
    metaData.legalBusinessName === undefined
  ) {
    return false
  }

  if (
    applicationCompleteRoutes.includes(mostRecentCompletedPage) &&
    applicationSubmitStatus === CaseTypes.PENDING_STIPULATIONS
  ) {
    if (
      metaData.portalAccess === undefined ||
      metaData.businessRegion === undefined
    ) {
      return false
    }

    if (isEmpty(contextData.offerMetaData)) {
      return false
    }

    const offerData = contextData.offerMetaData

    if (
      isEmpty(offerData.offerConstraints) ||
      isEmpty(offerData.averageMonthlyGrossSales) ||
      isEmpty(offerData.loanHistoryData)
    ) {
      return false
    }
  }

  return skipCall
}

export const determineSkipGetOffersCall = (contextData) => {
  let skipCall = true

  if (isEmpty(contextData)) {
    return false
  }

  if (contextData && isEmpty(contextData.offerMetaData)) {
    return false
  }

  const offerData = contextData.offerMetaData

  if (
    isEmpty(offerData.customOfferConstraints) ||
    isEmpty(offerData.productOfferConstraints)
  ) {
    return false
  }

  return skipCall
}

export const normalizeCreditApplicationResponse = (
  creditApplicationResponse,
) => {
  const {
    credit: {
      businessCredit: { application, applicationPrefillData },
    },
  } = creditApplicationResponse

  let responseData = mergeWith(
    {},
    applicationPrefillData,
    application,
    (objVal, srcVal) => {
      if (isNull(srcVal)) {
        return objVal
      }
      return undefined
    },
  )

  const normalizedCreditApplicationResponse = {
    legalBusinessName: responseData?.business?.legalBusinessName,
    firstName: responseData?.guarantor?.name.givenName,
    lastName: responseData?.guarantor?.name.surname,
    emailAddress: responseData?.guarantor?.emailAddress,
    primaryLoanPurpose: responseData?.loanPurpose,
    howDidYouHearAboutThisProduct: responseData?.productDiscovery?.source,
    doYouHaveAFundingCode: responseData?.productDiscovery?.invitationCode,
    homeAddress1: responseData?.guarantor?.address?.addressLine1,
    homeAddress2: responseData?.guarantor?.address?.addressLine2,
    city: responseData?.guarantor?.address?.adminArea2,
    state: responseData?.guarantor?.address?.adminArea1,
    zip: responseData?.guarantor?.address?.postalCode,
    personalPhoneNumber: getPhoneNumberFormat(
      responseData?.guarantor?.phones?.[0]?.nationalNumber,
    ),
    personalPhoneNumberType: responseData?.guarantor?.phones?.[0]?.type,
    businessAddress1: responseData?.business?.address?.addressLine1,
    businessAddress2: responseData?.business?.address?.addressLine2,
    businessCity: responseData?.business?.address?.adminArea2,
    businessState: responseData?.business?.address?.adminArea1,
    businessZip: responseData?.business?.address?.postalCode,
    businessPhoneNumber: getPhoneNumberFormat(
      responseData?.business?.phones?.[0]?.nationalNumber,
    ),
    businessWebAddress: responseData?.business?.websiteUrl,
    businessEntityType: responseData?.business?.organizationType,
    doingBusinessAs: responseData?.business?.doingBusinessAsName,
    stateOfIncorporation: responseData?.business?.incorporationRegion,
    totalAnnualBusinessRevenue:
      responseData?.business?.financialProfile?.totalAnnualBusinessRevenue
        ?.value,
    businessStartDate:
      responseData?.business?.formattedFormationDate?.numericMonthLongYear,
    numberOfFullTimeEmployees:
      responseData?.business?.numberOfFullTimeEmployees,
    businessIndustry: responseData?.business?.industryType,
    businessSubIndustry: responseData?.business?.subIndustryCode,
  }
  return normalizedCreditApplicationResponse
}

const generateDisclosurePlaceholders = (placeholdersObject) => {
  let placeholders = []
  placeholders = Object.keys(placeholdersObject).map((key) => {
    return {
      key,
      value: placeholdersObject[key].toString(),
      dataType: (typeof placeholdersObject[key]).toUpperCase(),
    }
  })

  return placeholders
}

export const disclosurePayloadGenerator = (
  creditApplicationId,
  placeholdersObject,
) => {
  return {
    applicationId: creditApplicationId,
    type: 'OFFER_DISCLOSURE', //disclosure query
    creditProductIdentifier: getProductIdentifier(
      serverData.startParams.product,
    ),
    placeholders: generateDisclosurePlaceholders(placeholdersObject),
  }
}

export const getPortalUrl = (portalInfo, caseType = CaseTypes.DEFAULT) => {
  if (portalInfo && portalInfo.url) {
    clientLogger.info('Fetched Portal URL from Portal Info - ', portalInfo.url)
    return portalInfo.url
  } else if (portalInfo && portalInfo.renewalRoutingUrls) {
    let redirectionUrl = portalInfo.renewalRoutingUrls.find((item) => {
      return item.type === caseType
    })

    if (redirectionUrl) {
      clientLogger.info(
        'Fetched Portal URL by matching the Case Type - ',
        redirectionUrl.endpoint,
      )
      return redirectionUrl.endpoint
    }

    redirectionUrl = portalInfo.renewalRoutingUrls.find((item) => {
      return item.type === CaseTypes.DEFAULT
    })
    clientLogger.info(
      'Fetched Portal URL by matching the default fallback Case Type - ',
      redirectionUrl.endpoint,
    )
    return redirectionUrl.endpoint
  }
  clientLogger.info(
    'Fetched Portal URL from config fallback - ',
    serverData.data.appConfig.application?.portalUrlFallback,
  )
  return serverData.data.appConfig.application?.portalUrlFallback
}

export const getProductName = (product) => {
  return getProductIdentifier(product) === 'PPBL_US'
    ? 'PayPal Business Loan'
    : 'Loanbuilder Loan'
}

// Adds number of days to current date and returns a date timestamp
export const getDayPlusNTimeStamp = (numberOfDaysToAdd) => {
  const now = new Date()
  const currentUTCTime = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds(),
    now.getUTCMilliseconds(),
  )
  return (currentUTCTime + numberOfDaysToAdd * 24 * 60 * 60 * 1000).toString()
}

export const storePartnerCampaignDetails = (partnerCampId) => {
  // store campaign data in local storage
  localStorage.setItem(PARTNER_CAMP_ID, partnerCampId)
  // add 2 days to current date
  localStorage.setItem(
    PARTNER_CAMP_EXPIRY,
    getDayPlusNTimeStamp(PARTNER_CAMP_EXPIRY_DAYS),
  )
}

export const storeUtmCampaignDetails = (utmCampaignId, utmCampaignDetails) => {
  sessionStorage.setItem(UTM_CAMPAIGN, utmCampaignId)
  if (utmCampaignDetails) {
    sessionStorage.setItem(UTM_CAMPAIGN_DETAILS, utmCampaignDetails)
  }
}

export const clearCampaignDetails = () => {
  sessionStorage.removeItem(UTM_CAMPAIGN)
  sessionStorage.removeItem(UTM_CAMPAIGN_DETAILS)
  localStorage.removeItem(PARTNER_CAMP_ID)
  localStorage.removeItem(PARTNER_CAMP_EXPIRY)
  sessionStorage.removeItem(LEAD_ORIGIN_URL)
}

export const clearSessionStorageExceptCampaign = () => {
  clientLogger.info('Clearing Session Storage expect Campaign')
  sessionStorage.removeItem('applicationPrefillData')
  sessionStorage.removeItem('renewalTokenBurnt')
  sessionStorage.removeItem('appId')
  sessionStorage.removeItem('mostRecentCompletedPage')
  sessionStorage.removeItem('applicationSubmitStatus')
}

export const handleGraphqlError = (
  error,
  gqlCallMessage = 'Error occurred',
  gqlCall,
  applicationId = null,
  renewalToken = null,
  legalBusinessName = null,
  applicationType,
) => {
  let redirectUrl = `/merchant-lending/loanbuilder/application/us/${applicationType}/error`
  let hasOpenApplication = false
  if (applicationId) {
    clientLogger.info(
      `Application Id for ${serverData?.startParams?.product}`,
      applicationId,
    )
  }

  if (serverData?.user) {
    clientLogger.info(
      'PayPal user Encrypted Account Number',
      serverData?.user?.encryptedAccountNumber,
    )
  }

  if (renewalToken) {
    clientLogger.info('Renewal user Renewal Token', renewalToken)
  }

  if (legalBusinessName) {
    clientLogger.info(
      'Legal Business Name for the application',
      legalBusinessName,
    )
  }

  if (error?.networkError) {
    if (
      errorConstants.SESSION_TIMEOUT_STATUS_CODES.includes(
        error?.networkError?.statusCode,
      )
    ) {
      clientLogger.warn(gqlCallMessage)
      clientLogger.warn(
        `GRAPHQL message while calling ${gqlCall}`,
        error?.message,
      )
      clientLogger.warn(
        `Session timed out while calling GraphQL endpoint with statusCode ${error?.networkError?.statusCode}, redirecting to session-timeout page`,
        JSON.stringify(error?.networkError),
      )
      redirectUrl = `/merchant-lending/loanbuilder/application/us/${applicationType}/session-timeout`
    } else if (
      error?.networkError?.result?.message ===
      errorConstants.USER_ACCOUNT_TYPE_UNSUPPORTED
    ) {
      clientLogger.warn('personal account error', error)
      clientLogger.warn(gqlCallMessage)
      clientLogger.warn(
        `GRAPHQL message while calling ${gqlCall}`,
        error?.message,
      )
      clientLogger.warn(
        'User Account Type is personal',
        serverData?.user?.encryptedAccountNumber,
      )
      redirectUrl = `/merchant-lending/loanbuilder/application/us/${applicationType}/invalid-account-type`
    } else {
      clientLogger.error(gqlCallMessage)
      clientLogger.error(
        `GRAPHQL error message while calling ${gqlCall}`,
        error?.message,
      )
      clientLogger.error(
        `GRAPHQL network error message with statusCode: ${error?.networkError?.statusCode} while calling ${gqlCall}`,
        JSON.stringify(error?.networkError?.result),
      )
    }
  }
  if (error?.graphQLErrors?.length > 0) {
    error?.graphQLErrors.forEach((individualError) => {
      const { extensions } = individualError
      if (
        extensions &&
        extensions.details &&
        Array.isArray(extensions.details)
      ) {
        hasOpenApplication = extensions.details.some(
          (detail) => detail.issue === errorConstants.OPEN_APPLICATIONS_EXIST,
        )
      }
      if (hasOpenApplication) {
        clientLogger.warn(gqlCallMessage)
        clientLogger.warn(
          `GRAPHQL message while calling ${gqlCall}`,
          error?.message,
        )
        clientLogger.warn(
          `Open Application exists for this user - Business Name: ${legalBusinessName}`,
          individualError?.message,
        )
        redirectUrl = `/merchant-lending/loanbuilder/application/us/${applicationType}/open-application`
      } else {
        clientLogger.error(gqlCallMessage)
        clientLogger.error(
          `GRAPHQL errors while calling ${gqlCall}`,
          individualError?.message,
        )
      }
    })
  }
  if (error?.clientErrors?.length > 0) {
    error?.clientErrors.forEach((individualError) => {
      clientLogger.error(gqlCallMessage)
      clientLogger.error(
        `GRAPHQL client errors while calling ${gqlCall}`,
        individualError?.message,
      )
    })
  }
  navigate(redirectUrl)
}
